import { z } from "zod";
import { billingNoticeNotificationKindSchema } from "./billing-notices.js";
import { feedThreadSubjectTypeSchema } from "./channel-feed.js";
import { connectorProviderSchema } from "./connectors.js";
import { scheduledSessionPauseReasonSchema } from "./scheduled-sessions.js";
import { sessionApprovalDecisionSchema } from "./sessions.js";
import { twinShareSubjectKindSchema } from "./twin-shares.js";
import { matcherReviewerUnavailableSchema } from "./twin-matching.js";
import { workspaceTwinProposalDecidedContextSchema } from "./twin-shared-review.js";
const identifierSchema = z.string().trim().min(1).max(500);
const actorIdentifierSchema = z.string().trim().min(1).max(200);
const optionalLabelSchema = z.string().trim().min(1).max(200).nullable();
const optionalRecordTitleSchema = z.string().trim().min(1).max(500).nullable();
const previewSchema = z.string().max(180).nullable();
const actorSchema = z.object({
    type: z.enum(["user", "agent", "service"]),
    id: actorIdentifierSchema,
}).strict();
const recipientIdsSchema = z.array(identifierSchema).max(200).superRefine((values, context) => {
    if (new Set(values).size !== values.length) {
        context.addIssue({ code: "custom", message: "Recipient IDs must be unique" });
    }
});
/**
 * Internal, versioned context stored beside a thin workspace event. Generic
 * events deliberately carry no payload: the canonical resource identity stays
 * on workspace_events and the notification projector records a no-op.
 */
export const workspaceResourceChangedContextSchema = z.object({
    eventKind: z.literal("workspace.resource_changed"),
    schemaVersion: z.literal(1),
    payload: z.object({}).strict(),
}).strict();
const postPayloadSchema = z.object({
    postId: identifierSchema,
    channelId: identifierSchema,
    actor: actorSchema,
    actorDisplayName: optionalLabelSchema.optional(),
    newlyMentionedUserIds: recipientIdsSchema,
    channelName: optionalLabelSchema,
    preview: previewSchema,
}).strict();
export const workspacePostCreatedContextSchema = z.object({
    eventKind: z.literal("workspace.post.created"),
    schemaVersion: z.literal(1),
    payload: postPayloadSchema,
}).strict();
export const workspacePostUpdatedContextSchema = z.object({
    eventKind: z.literal("workspace.post.updated"),
    schemaVersion: z.literal(1),
    payload: postPayloadSchema,
}).strict();
const threadCommentPayloadSchema = z.object({
    threadId: identifierSchema,
    commentId: identifierSchema,
    /** Null for a thread with no channel: a matched pair's, an entity's or a share's. */
    channelId: identifierSchema.nullable(),
    actor: actorSchema,
    actorDisplayName: optionalLabelSchema.optional(),
    newlyMentionedUserIds: recipientIdsSchema,
    discussionOwnerUserId: identifierSchema.nullable(),
    subject: z.object({
        type: feedThreadSubjectTypeSchema,
        id: identifierSchema,
    }).strict(),
    channelName: optionalLabelSchema,
    preview: previewSchema,
}).strict();
export const workspaceThreadCommentCreatedContextSchema = z.object({
    eventKind: z.literal("workspace.thread_comment.created"),
    schemaVersion: z.literal(1),
    payload: threadCommentPayloadSchema,
}).strict();
export const workspaceThreadCommentUpdatedContextSchema = z.object({
    eventKind: z.literal("workspace.thread_comment.updated"),
    schemaVersion: z.literal(1),
    payload: threadCommentPayloadSchema,
}).strict();
export const workspaceReactionCreatedContextSchema = z.object({
    eventKind: z.literal("workspace.reaction.created"),
    schemaVersion: z.literal(1),
    payload: z.object({
        threadId: identifierSchema,
        commentId: identifierSchema.nullable(),
        /** Null for a thread with no channel: a matched pair's or an entity's. */
        channelId: identifierSchema.nullable(),
        actor: actorSchema,
        recipientUserId: identifierSchema,
        subject: z.object({ type: feedThreadSubjectTypeSchema, id: identifierSchema }).strict(),
        channelName: optionalLabelSchema,
        preview: previewSchema,
    }).strict(),
}).strict();
const recordPayloadSchema = z.object({
    recordId: identifierSchema,
    recordEventId: identifierSchema,
    objectTypeId: identifierSchema,
    channelId: identifierSchema,
    actor: actorSchema,
    title: optionalRecordTitleSchema,
    newlyAssignedUserIds: z.array(identifierSchema).optional(),
    actorDisplayName: optionalLabelSchema.optional(),
}).strict();
export const workspaceRecordCreatedContextSchema = z.object({
    eventKind: z.literal("workspace.record.created"),
    schemaVersion: z.literal(1),
    payload: recordPayloadSchema,
}).strict();
export const workspaceRecordUpdatedContextSchema = z.object({
    eventKind: z.literal("workspace.record.updated"),
    schemaVersion: z.literal(1),
    payload: recordPayloadSchema,
}).strict();
export const workspaceRecordArchivedContextSchema = z.object({
    eventKind: z.literal("workspace.record.archived"),
    schemaVersion: z.literal(1),
    payload: recordPayloadSchema,
}).strict();
const approvalPendingPayloadSchema = z.object({
    approvalId: identifierSchema,
    sessionId: identifierSchema,
    recipientUserId: identifierSchema,
    actor: actorSchema,
    toolName: optionalLabelSchema,
}).strict();
export const workspaceApprovalPendingContextSchema = z.object({
    eventKind: z.literal("workspace.approval.pending"),
    schemaVersion: z.literal(1),
    payload: approvalPendingPayloadSchema,
}).strict();
const approvalResolvedPayloadSchema = z.object({
    approvalId: identifierSchema,
    sessionId: identifierSchema,
    recipientUserId: identifierSchema,
    decision: sessionApprovalDecisionSchema,
}).strict();
export const workspaceApprovalResolvedContextSchema = z.object({
    eventKind: z.literal("workspace.approval.resolved"),
    schemaVersion: z.literal(1),
    payload: approvalResolvedPayloadSchema,
}).strict();
export const workspaceChannelAccessGrantedContextSchema = z.object({
    eventKind: z.literal("workspace.channel_access.granted"),
    schemaVersion: z.literal(1),
    payload: z.object({
        channelId: identifierSchema,
        recipientUserId: identifierSchema,
        actor: actorSchema,
        role: z.enum(["owner", "member"]),
        channelName: optionalLabelSchema,
    }).strict(),
}).strict();
const pluginConnectionIncidentPayloadSchema = z.object({
    incidentId: identifierSchema,
    recipientUserId: identifierSchema,
    pluginSlug: identifierSchema,
    state: z.enum(["permission_missing", "grant_rejected"]),
}).strict();
export const workspacePluginConnectionAttentionContextSchema = z.object({
    eventKind: z.literal("workspace.plugin_connection.attention_required"),
    schemaVersion: z.literal(1), payload: pluginConnectionIncidentPayloadSchema,
}).strict();
export const workspacePluginConnectionResolvedContextSchema = z.object({
    eventKind: z.literal("workspace.plugin_connection.attention_resolved"),
    schemaVersion: z.literal(1), payload: pluginConnectionIncidentPayloadSchema,
}).strict();
export const workspaceConnectorJobFailedContextSchema = z.object({
    eventKind: z.literal("workspace.connector_job.failed"),
    schemaVersion: z.literal(1),
    payload: z.object({
        connectorJobId: identifierSchema,
        recipientUserId: identifierSchema.nullable(),
        actor: actorSchema,
        provider: connectorProviderSchema,
    }).strict(),
}).strict();
const inboxAttentionIdentityPayloadSchema = z.object({
    inboxItemId: identifierSchema,
    recipientUserId: identifierSchema,
}).strict();
export const workspaceInboxAttentionRequiredContextSchema = z.object({
    eventKind: z.literal("workspace.inbox.attention_required"),
    schemaVersion: z.literal(1),
    payload: inboxAttentionIdentityPayloadSchema.extend({
        actor: actorSchema,
        source: connectorProviderSchema,
        sender: optionalLabelSchema,
        preview: previewSchema,
    }).strict(),
}).strict();
export const workspaceInboxAttentionResolvedContextSchema = z.object({
    eventKind: z.literal("workspace.inbox.attention_resolved"),
    schemaVersion: z.literal(1),
    payload: inboxAttentionIdentityPayloadSchema.extend({
        resolution: z.enum(["dismissed", "promoted", "deleted"]),
    }).strict(),
}).strict();
const taskRunOutcomePayloadSchema = z.object({
    taskRunId: identifierSchema,
    sessionId: identifierSchema,
    recipientUserId: identifierSchema,
    actor: actorSchema,
    title: optionalLabelSchema,
}).strict();
export const workspaceTaskFailedContextSchema = z.object({
    eventKind: z.literal("workspace.task.failed"),
    schemaVersion: z.literal(1),
    payload: taskRunOutcomePayloadSchema,
}).strict();
export const workspaceTaskCompletedContextSchema = z.object({
    eventKind: z.literal("workspace.task.completed"),
    schemaVersion: z.literal(1),
    payload: taskRunOutcomePayloadSchema.extend({
        /** The run answered a question whose answer was asked for in the Inbox (twin.query.create's `delivery`). */
        delivery: z.literal("inbox").optional(),
    }).strict(),
}).strict();
const automationRunPayloadSchema = taskRunOutcomePayloadSchema.extend({
    automationId: identifierSchema,
}).strict();
export const workspaceAutomationAttentionRequiredContextSchema = z.object({
    eventKind: z.literal("workspace.automation.attention_required"),
    schemaVersion: z.literal(1),
    payload: automationRunPayloadSchema.extend({
        consecutiveFailureCount: z.number().int().positive(),
        firstFailureAt: z.number().int().nonnegative(),
        lastFailureAt: z.number().int().nonnegative(),
    }).strict(),
}).strict();
export const workspaceAutomationCompletedContextSchema = z.object({
    eventKind: z.literal("workspace.automation.completed"),
    schemaVersion: z.literal(1),
    payload: automationRunPayloadSchema,
}).strict();
/** The system paused an automation its owner may no longer run as it is. */
export const workspaceAutomationPausedContextSchema = z.object({
    eventKind: z.literal("workspace.automation.paused"),
    schemaVersion: z.literal(1),
    payload: z.object({
        automationId: identifierSchema,
        recipientUserId: identifierSchema,
        actor: actorSchema,
        title: optionalLabelSchema,
        pauseReason: scheduledSessionPauseReasonSchema,
    }).strict(),
}).strict();
/** A workflow stopped admitting work after its trigger repeatedly failed. */
export const workspaceWorkflowAttentionRequiredContextSchema = z.object({
    eventKind: z.literal("workspace.workflow.attention_required"),
    schemaVersion: z.literal(1),
    payload: z.object({
        workflowId: identifierSchema,
        activationId: identifierSchema,
        sessionId: identifierSchema,
        recipientUserId: identifierSchema,
        actor: actorSchema,
        title: optionalLabelSchema,
        failureCode: z.string().min(1).max(250).regex(/^[A-Za-z0-9_:.-]+$/u),
        consecutiveFailureCount: z.number().int().positive(),
    }).strict(),
}).strict();
/**
 * A batch of Twin proposals left items for a person to decide. One event per
 * recipient, so the notification projector treats it like any other attention
 * request.
 */
export const workspaceTwinBatchReviewRequiredContextSchema = z.object({
    eventKind: z.literal("workspace.twin_batch.review_required"),
    schemaVersion: z.literal(1),
    payload: z.object({
        batchId: identifierSchema,
        kind: z.enum(["link", "definition", "action", "workflow_review", "channel_widget_change"]),
        sourceId: identifierSchema,
        sourceLabel: z.string().trim().min(1).max(200),
        recipientUserId: identifierSchema,
        actor: actorSchema,
        /** Proposals waiting for a decision; how many carry a contradiction; automatic ones held back. */
        toReview: z.number().int().nonnegative(),
        contradictions: z.number().int().nonnegative(),
        held: z.number().int().nonnegative(),
        /** Proposals this batch added, as opposed to re-scored ones that were already waiting. */
        newToReview: z.number().int().nonnegative().default(0),
        /**
         * The matcher names reviewers and none of them can review its cards now, each for this reason: the notice goes to
         * the matcher's authors so they can name someone, and until then only an admin decides the cards.
         */
        reviewersUnavailable: z.array(matcherReviewerUnavailableSchema).min(1).max(64).optional(),
    }).strict(),
}).strict();
/** The batch no longer needs this person: everything was decided, or a newer batch took over. */
export const workspaceTwinBatchReviewResolvedContextSchema = z.object({
    eventKind: z.literal("workspace.twin_batch.review_resolved"),
    schemaVersion: z.literal(1),
    payload: z.object({
        batchId: identifierSchema,
        kind: z.enum(["link", "definition", "action", "workflow_review", "channel_widget_change"]),
        recipientUserId: identifierSchema,
        resolution: z.enum(["reviewed", "superseded"]),
    }).strict(),
}).strict();
/**
 * A comment on a canvas that mentions people. The event is the canvas's task's, so only the people
 * who can read the canvas see it; the session is the one the canvas opens beside.
 */
export const workspaceCanvasCommentCreatedContextSchema = z.object({
    eventKind: z.literal("workspace.canvas_comment.created"),
    schemaVersion: z.literal(1),
    payload: z.object({
        taskId: identifierSchema,
        sessionId: identifierSchema,
        artifactId: identifierSchema,
        annotationId: identifierSchema,
        channelId: identifierSchema.nullable(),
        actor: actorSchema,
        actorDisplayName: optionalLabelSchema,
        newlyMentionedUserIds: recipientIdsSchema,
        title: optionalRecordTitleSchema,
        channelName: optionalLabelSchema,
        preview: previewSchema,
    }).strict(),
}).strict();
/**
 * An attention rule matched a record: one event per recipient, as for a review. The item stays open while the rule's
 * condition holds; `ruleLabel` names the rule's items together ("Overdue invoices"). A list row reads `detail` under the
 * title and `figure`, the record's key amount, at its right; `reason` is the whole sentence, for messages.
 */
export const workspaceTwinAttentionRaisedContextSchema = z.object({
    eventKind: z.literal("workspace.twin_attention.raised"),
    schemaVersion: z.literal(1),
    payload: z.object({
        itemId: identifierSchema,
        ruleId: identifierSchema,
        ruleLabel: z.string().trim().min(1).max(120),
        title: z.string().trim().min(1).max(200),
        reason: z.string().trim().min(1).max(300),
        /** "£650.00", or absent when the record has no amount. */
        figure: z.string().trim().min(1).max(40).optional(),
        /** "City Limousines · due 19 Sep 2026", without the amount. */
        detail: z.string().trim().min(1).max(200).optional(),
        entityId: identifierSchema.nullable(),
        entityName: optionalLabelSchema,
        entityType: optionalLabelSchema,
        recipientUserId: identifierSchema,
        actor: actorSchema,
    }).strict(),
}).strict();
/** The item no longer needs this person: its condition cleared, or someone said never for its entity. */
export const workspaceTwinAttentionResolvedContextSchema = z.object({
    eventKind: z.literal("workspace.twin_attention.resolved"),
    schemaVersion: z.literal(1),
    payload: z.object({
        itemId: identifierSchema,
        ruleId: identifierSchema,
        recipientUserId: identifierSchema,
        resolution: z.enum(["cleared", "never"]),
    }).strict(),
}).strict();
/**
 * A billing notice opened: one event per organisation admin. `aiPaused` says used-up credits have paused new AI work,
 * which only a plan that enforces does.
 */
export const workspaceBillingNoticeRaisedContextSchema = z.object({
    eventKind: z.literal("workspace.billing_notice.raised"),
    schemaVersion: z.literal(1),
    payload: z.object({
        noticeId: identifierSchema,
        kind: billingNoticeNotificationKindSchema,
        aiPaused: z.boolean(),
        recipientUserId: identifierSchema,
    }).strict(),
}).strict();
/** The notice no longer needs this person: credits were added, the month ended, or the payment went through. */
export const workspaceBillingNoticeResolvedContextSchema = z.object({
    eventKind: z.literal("workspace.billing_notice.resolved"),
    schemaVersion: z.literal(1),
    payload: z.object({
        noticeId: identifierSchema,
        recipientUserId: identifierSchema,
    }).strict(),
}).strict();
/** A share's notices carry ids only, never its title or text (docs/specs/twin-shares.md, section 5; finding 3). */
const shareActorSchema = z.object({ type: z.literal("user"), id: actorIdentifierSchema }).strict();
/** A share made for people, or people added to one: each of them hears of it, as `share_received`. */
export const workspaceShareReceivedContextSchema = z.object({
    eventKind: z.literal("workspace.share.received"),
    schemaVersion: z.literal(1),
    payload: z.object({
        shareId: identifierSchema,
        actor: shareActorSchema,
        actorDisplayName: optionalLabelSchema.optional(),
        addedUserIds: z.array(identifierSchema).min(1).max(20),
        subjectKind: twinShareSubjectKindSchema,
    }).strict(),
}).strict();
/** A people share moved to a channel: each person who was in it hears where it went, in the app (`share_moved`). */
export const workspaceShareMovedContextSchema = z.object({
    eventKind: z.literal("workspace.share.moved"),
    schemaVersion: z.literal(1),
    payload: z.object({
        shareId: identifierSchema,
        actor: shareActorSchema,
        actorDisplayName: optionalLabelSchema.optional(),
        channelId: identifierSchema,
        channelName: optionalLabelSchema,
        formerPeopleIds: z.array(identifierSchema).max(20),
    }).strict(),
}).strict();
/**
 * A shared mailbox's conversation assigned to someone who can read the mailbox (docs/specs/twin-shares.md, section 3):
 * they hear of it in the app, as `mailbox_assignment`. Ids only: the conversation, and its newest message, the email
 * the notice opens.
 */
export const workspaceMailboxAssignedContextSchema = z.object({
    eventKind: z.literal("workspace.mailbox.assigned"),
    schemaVersion: z.literal(1),
    payload: z.object({
        conversationKey: identifierSchema,
        inboxItemId: identifierSchema.nullable(),
        actor: shareActorSchema,
        actorDisplayName: optionalLabelSchema.optional(),
        assigneeUserId: identifierSchema,
    }).strict(),
}).strict();
export const workspaceEventContextSchema = z.discriminatedUnion("eventKind", [
    workspaceResourceChangedContextSchema,
    workspaceReactionCreatedContextSchema,
    workspacePostCreatedContextSchema,
    workspacePostUpdatedContextSchema,
    workspaceThreadCommentCreatedContextSchema,
    workspaceThreadCommentUpdatedContextSchema,
    workspaceCanvasCommentCreatedContextSchema,
    workspaceRecordCreatedContextSchema,
    workspaceRecordUpdatedContextSchema,
    workspaceRecordArchivedContextSchema,
    workspaceApprovalPendingContextSchema,
    workspaceApprovalResolvedContextSchema,
    workspaceChannelAccessGrantedContextSchema,
    workspaceConnectorJobFailedContextSchema,
    workspacePluginConnectionAttentionContextSchema,
    workspacePluginConnectionResolvedContextSchema,
    workspaceInboxAttentionRequiredContextSchema,
    workspaceInboxAttentionResolvedContextSchema,
    workspaceTaskFailedContextSchema,
    workspaceTaskCompletedContextSchema,
    workspaceAutomationAttentionRequiredContextSchema,
    workspaceAutomationCompletedContextSchema,
    workspaceAutomationPausedContextSchema,
    workspaceWorkflowAttentionRequiredContextSchema,
    workspaceTwinBatchReviewRequiredContextSchema,
    workspaceTwinBatchReviewResolvedContextSchema,
    workspaceTwinProposalDecidedContextSchema,
    workspaceTwinAttentionRaisedContextSchema,
    workspaceTwinAttentionResolvedContextSchema,
    workspaceBillingNoticeRaisedContextSchema,
    workspaceBillingNoticeResolvedContextSchema,
    workspaceShareReceivedContextSchema,
    workspaceShareMovedContextSchema,
    workspaceMailboxAssignedContextSchema,
]);
export const genericWorkspaceEventContext = {
    eventKind: "workspace.resource_changed",
    schemaVersion: 1,
    payload: {},
};
